import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { FrontendLoggerService } from '../services/frontend-logger.service';
import { filter, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  private getRequestedUrl(state: RouterStateSnapshot): string {
    const stateUrl = (state.url || '').trim();
    if (stateUrl && stateUrl !== '/') {
      return stateUrl;
    }

    const pathname = globalThis.location?.pathname ?? '/';
    const search = globalThis.location?.search ?? '';
    const resolvedUrl = `${pathname}${search}`;

    return resolvedUrl || '/';
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ) {
    const loading$ = this.authService.isLoading$;
    const auth$ = this.authService.isAuthenticated$;
    const wiz$ = this.authService.isWizardCompleted();
    return combineLatest({ loading: loading$, isAuthenticated: auth$, wizardDone: wiz$ }).pipe(
      filter(({ loading }) => loading === false),
      take(1),
      map(({ isAuthenticated, wizardDone }) => {
        const target = (state.url || '').split('?')[0];
        const inviteTarget = this.authService.getPendingInviteTarget();
        const resolvedFlow = this.authService.getResolvedFlow();
        this.frontendLogger.debug(`[AuthGuard] canActivate for ${target} isAuthenticated=${isAuthenticated} wizardDone=${wizardDone} resolvedFlow=${resolvedFlow}`);
        // Allow public invite onboarding route without authentication
        if (target === '/onboarding') {
          this.frontendLogger.debug(`[AuthGuard] Allowing public onboarding route ${target}`);
          return true;
        }
        if (!isAuthenticated) {
          this.frontendLogger.warn('[AuthGuard] Not authenticated. Redirecting to login');
          this.router.navigate(['/login'], { queryParams: { returnUrl: this.getRequestedUrl(state) } });
          return false;
        }
        if (wizardDone && resolvedFlow) {
          this.authService.clearResolvedFlow();
        }
        if (resolvedFlow === 'registered') {
          if (target === '/wizard') {
            this.router.navigate(['/chat']);
            return false;
          }
          return true;
        }
        if (resolvedFlow === 'invited' && inviteTarget && target !== '/onboarding') {
          this.frontendLogger.info('[AuthGuard] Backend resolved invited flow. Redirecting to invite onboarding');
          this.router.navigateByUrl(inviteTarget);
          return false;
        }
        if (resolvedFlow === 'new_user' && target !== '/wizard') {
          this.frontendLogger.info('[AuthGuard] Backend resolved new_user flow. Redirecting to /wizard');
          this.router.navigate(['/wizard']);
          return false;
        }
        if (!wizardDone && inviteTarget && target !== '/onboarding') {
          this.frontendLogger.info('[AuthGuard] Pending invite context detected. Redirecting to invite onboarding');
          this.router.navigateByUrl(inviteTarget);
          return false;
        }
        if (!wizardDone && target !== '/wizard') {
          this.frontendLogger.warn('[AuthGuard] Wizard not completed. Redirecting to /wizard');
          this.router.navigate(['/wizard']);
          return false;
        }
        // Opcional: si el wizard ya está complete y navega al wizard, redirigir a chat
        if (wizardDone && target === '/wizard') {
          this.frontendLogger.info('[AuthGuard] Wizard already completed. Redirecting to /chat');
          this.router.navigate(['/chat']);
          return false;
        }
        this.frontendLogger.debug(`[AuthGuard] Access granted to ${target}`);
        return true;
      })
    );
  }
}
